import React, { useMemo, useState } from "react";
import Swal from "sweetalert2";
import DataTableComponentFrontPagination from "../../common/DataTableComponentFrontPagination";
import TextInput from "../../library/TextInput";
import SelectDropdown from "../../library/SelectDropdown";
import ResizableTextarea from "../../library/ResizableTextarea";
import Button from "../../library/Button";
import PositiveButton from "../../library/PositiveButton";
import ButtonGroup from "../../library/ButtonGroup";
import CommonButton from "../../common/Button/Button";
import { DeleteOhsBuildingCheckQuestion, SaveOhsBuildingCheckQuestion } from "../../../services/FirstAidBoxService";
import { invalidateFirstAidBoxQueries, useOhsBuildingCheckQuestions } from "../../../query/hooks/QueryHooksFirstAidBox";
import { getOhsErrorMessage, numberChecklistQuestions } from "./ohsUtils";
import { BUILDING_CHECK_ANSWER_LABELS, BUILDING_CHECK_ANSWERS, EXPECTED_ANSWER_OPTIONS } from "./ohsConstants";
import { FormGrid, SectionCard, SectionHeader, SectionTitle } from "./Ohs.styles";

const EMPTY_QUESTION = { id: null, section: "", question: "", expectedAnswer: BUILDING_CHECK_ANSWERS.yes };
const EXPORT_HEADER = ["No.", "Section", "Question", "Expected Answer"];

/**
 * Building checklist - the questions every monthly building check answers, in form order. New questions go
 * to the end of their section; editing or deleting one never changes past checks, which keep their
 * own copy of what was asked.
 */
function BuildingQuestionEditor() {
    const { data: checklist, isLoading } = useOhsBuildingCheckQuestions();
    const [search, setSearch] = useState("");
    const [editingQuestion, setEditingQuestion] = useState(null);
    const [formErrors, setFormErrors] = useState({});
    const [isSaving, setIsSaving] = useState(false);

    const numberedQuestions = useMemo(() => numberChecklistQuestions(checklist), [checklist]);
    const sectionNames = useMemo(() => [...new Set(numberedQuestions.map((checklistQuestion) => checklistQuestion.section))], [numberedQuestions]);

    const filteredQuestions = useMemo(() => {
        const searchText = search.trim().toLowerCase();
        if (!searchText) {
            return numberedQuestions;
        }
        return numberedQuestions.filter((checklistQuestion) => {
            return checklistQuestion.question.toLowerCase().includes(searchText)
                || checklistQuestion.section.toLowerCase().includes(searchText);
        });
    }, [numberedQuestions, search]);

    const setEditingField = (fieldName, value) => {
        setEditingQuestion((currentQuestion) => ({ ...currentQuestion, [fieldName]: value }));
        setFormErrors((currentErrors) => ({ ...currentErrors, [fieldName]: undefined }));
    };

    const handleSave = async () => {
        const requiredErrors = {
            section: editingQuestion.section.trim() ? undefined : "Section is required",
            question: editingQuestion.question.trim() ? undefined : "Question is required",
        };
        if (Object.values(requiredErrors).some(Boolean)) {
            setFormErrors(requiredErrors);
            return;
        }

        setIsSaving(true);
        try {
            // A section typed with different casing or spacing still joins the existing one.
            const typedSection = editingQuestion.section.trim();
            const matchingSection = sectionNames.find((sectionName) => sectionName.toLowerCase() === typedSection.toLowerCase());
            await SaveOhsBuildingCheckQuestion({
                id: editingQuestion.id || undefined,
                section: matchingSection || typedSection,
                question: editingQuestion.question.trim(),
                expectedAnswer: editingQuestion.expectedAnswer,
            });
            await invalidateFirstAidBoxQueries();
            setEditingQuestion(null);
        } catch (error) {
            console.log("BuildingQuestionEditor.js->handleSave->Error", error);
            Swal.fire("Error", getOhsErrorMessage(error, "The question could not be saved."), "error");
        } finally {
            setIsSaving(false);
        }
    };

    const handleDelete = async (checklistQuestion) => {
        try {
            const confirmation = await Swal.fire({
                title: "Are you sure?",
                text: `Question ${checklistQuestion.number} will be removed from the building checklist. Past building checks keep their answer to it.`,
                icon: "warning",
                showCancelButton: true,
                confirmButtonColor: "var(--button-color)",
                cancelButtonColor: "var(--states-alert)",
                confirmButtonText: "Yes, delete it!",
            });
            if (!confirmation.isConfirmed) {
                return;
            }

            await DeleteOhsBuildingCheckQuestion(checklistQuestion.id);
            await invalidateFirstAidBoxQueries();
            Swal.fire("Deleted!", "The question has been deleted.", "success");
        } catch (error) {
            console.log("BuildingQuestionEditor.js->handleDelete->Error", error);
            Swal.fire("Error", getOhsErrorMessage(error, "The question could not be deleted."), "error");
        }
    };

    const columns = [
        {
            name: "No.",
            selector: "number",
            sortable: true,
            width: "80px",
            cell: (row) => row.number,
        },
        {
            name: "Section",
            selector: "section",
            sortable: true,
            minWidth: "180px",
            cell: (row) => row.section,
        },
        {
            name: "Question",
            selector: "question",
            sortable: true,
            grow: 4,
            minWidth: "320px",
            cell: (row) => <span title={row.question}>{row.question}</span>,
        },
        {
            name: "Expected Answer",
            selector: "expected_answer",
            sortable: true,
            minWidth: "140px",
            cell: (row) => BUILDING_CHECK_ANSWER_LABELS[row.expected_answer],
        },
        {
            name: "Action",
            width: "110px",
            right: true,
            cell: (row) => (
                <div className="assessment-08 btn-dropdown-grp">
                    <div className="as-buttons">
                        <button
                            type="button"
                            className="btn btn-primary rounded-circle"
                            title="Edit"
                            onClick={() => {
                                setFormErrors({});
                                setEditingQuestion({ id: row.id, section: row.section, question: row.question, expectedAnswer: row.expected_answer });
                            }}
                        >
                            <i className="fal fa-edit"></i>
                        </button>
                        <button
                            type="button"
                            className="btn btn-danger rounded-circle"
                            title="Delete"
                            onClick={() => handleDelete(row)}
                        >
                            <i className="fal fa-trash-alt"></i>
                        </button>
                    </div>
                </div>
            ),
        },
    ];

    /**
     * Rows for the CSV/XLSX/PDF export, in form order.
     * @returns {Object} { header, data, pdfData }
     */
    const exportData = () => {
        const exportRows = filteredQuestions.map((checklistQuestion) => [
            checklistQuestion.number,
            checklistQuestion.section,
            checklistQuestion.question,
            BUILDING_CHECK_ANSWER_LABELS[checklistQuestion.expected_answer],
        ]);

        return {
            header: EXPORT_HEADER,
            data: exportRows.map((exportRow) => Object.fromEntries(EXPORT_HEADER.map((columnName, columnIndex) => [columnName, exportRow[columnIndex]]))),
            pdfData: exportRows,
        };
    };

    return (
        <>
            {editingQuestion && (
                <SectionCard>
                    <SectionHeader>
                        <SectionTitle>{editingQuestion.id ? "Edit Question" : "Add Question"}</SectionTitle>
                    </SectionHeader>
                    <FormGrid>
                        <TextInput
                            id="ohsChecklistSection"
                            label="Section"
                            required
                            maxLength={80}
                            placeholder="e.g. Working Environment"
                            value={editingQuestion.section}
                            onChange={(event) => setEditingField("section", event.target.value)}
                            error={Boolean(formErrors.section)}
                            errorText={formErrors.section}
                            helperText={sectionNames.length ? `Use an existing section to add to it: ${sectionNames.join(", ")}.` : undefined}
                        />
                        <SelectDropdown
                            id="ohsChecklistExpectedAnswer"
                            label="Expected Answer"
                            required
                            options={EXPECTED_ANSWER_OPTIONS}
                            value={editingQuestion.expectedAnswer}
                            onChange={(event) => setEditingField("expectedAnswer", event.target.value)}
                            helperText="The answer that means all is well. Any other answer (except N/A) is an issue and needs a comment."
                            isSearchable={false}
                        />
                        <div className="ohs-field--full">
                            <ResizableTextarea
                                id="ohsChecklistQuestion"
                                label="Question"
                                required
                                rows={3}
                                maxLength={500}
                                placeholder="e.g. Are emergency exits available and easily accessible?"
                                value={editingQuestion.question}
                                onChange={(event) => setEditingField("question", event.target.value)}
                                errorText={formErrors.question}
                            />
                        </div>
                    </FormGrid>
                    <ButtonGroup>
                        <PositiveButton onClick={handleSave} disabled={isSaving} />
                        <CommonButton type="button" variant="danger" onClick={() => setEditingQuestion(null)} iconLeft={<i className="fal fa-times"></i>}>
                            Cancel
                        </CommonButton>
                    </ButtonGroup>
                </SectionCard>
            )}

            <DataTableComponentFrontPagination
                data={filteredQuestions}
                columns={columns}
                loading={isLoading}
                keyField="id"
                search={search}
                setSearch={setSearch}
                exportData={exportData}
                exportFileName="OHS_Building_Checklist"
                defaultSort={{ defaultSortColumn: "number", defaultSortAsc: true }}
                tableButton={[
                    <ButtonGroup key="checklist-actions">
                        <Button
                            onClick={() => {
                                setFormErrors({});
                                setEditingQuestion(EMPTY_QUESTION);
                            }}
                            disabled={Boolean(editingQuestion)}
                            title="Add question"
                        >
                            <i className="fal fa-plus"></i>
                            Add Question
                        </Button>
                    </ButtonGroup>,
                ]}
            />
        </>
    );
}

export default BuildingQuestionEditor;
